/**
 * Módulo para corregir la estructura de los Comprobantes Fiscales Electrónicos (e-CF)
 * según los esquemas XSD oficiales de la DGII
 */

/**
 * Corrige la estructura del elemento 'Totales' en un e-CF según el tipo de comprobante
 * @param {Object} ecfData - Datos del e-CF en formato JSON
 * @returns {Object} - Datos del e-CF con estructura corregida
 */
function correctECFStructure(ecfData) {
  // Verificar si los datos son válidos
  if (!ecfData || !ecfData.ECF || !ecfData.ECF.Encabezado) {
    console.error('Los datos del e-CF no son válidos para corregir');
    return ecfData;
  }

  // Obtener el tipo de e-CF
  const tipoECF = ecfData.ECF.Encabezado.IdDoc?.TipoeCF || "31";

  // Crear una copia profunda de los datos para no modificar el original
  const correctedData = JSON.parse(JSON.stringify(ecfData));

  // Corregir la estructura según el tipo de e-CF
  switch (tipoECF) {
    case "31":
      correctedData.ECF.Encabezado.Totales = correctTotales31(correctedData.ECF.Encabezado.Totales);
      break;
    case "32":
      correctedData.ECF.Encabezado.Totales = correctTotales32(correctedData.ECF.Encabezado.Totales);
      break;
    case "33":
      correctedData.ECF.Encabezado.Totales = correctTotales33(correctedData.ECF.Encabezado.Totales);
      break;
    case "34":
      correctedData.ECF.Encabezado.Totales = correctTotales34(
        correctedData.ECF.Encabezado.Totales,
        correctedData.ECF
      );
      break;
    case "41":
      correctedData.ECF.Encabezado.Totales = correctTotales41(correctedData.ECF.Encabezado.Totales);
      break;
    case "43":
      correctedData.ECF.Encabezado.Totales = correctTotales43(correctedData.ECF.Encabezado.Totales);
      break;
    case "44":
      correctedData.ECF.Encabezado.Totales = correctTotales44(correctedData.ECF.Encabezado.Totales);
      break;
    case "45":
      correctedData.ECF.Encabezado.Totales = correctTotales45(correctedData.ECF.Encabezado.Totales);
      break;
    case "46":
      correctedData.ECF.Encabezado.Totales = correctTotales46(correctedData.ECF.Encabezado.Totales);
      break;
    case "47":
      correctedData.ECF.Encabezado.Totales = correctTotales47(correctedData.ECF.Encabezado.Totales);
      break;
    default:
      // Si no hay corrección específica, mantener la estructura básica
      correctedData.ECF.Encabezado.Totales = correctTotalesBasic(correctedData.ECF.Encabezado.Totales);
      break;
  }

  return correctedData;
}

/**
 * Corrige la estructura básica del elemento 'Totales' para cualquier tipo de e-CF
 * @param {Object} totales - Datos de la sección Totales
 * @returns {Object} - Datos corregidos
 */
function correctTotalesBasic(totales) {
  if (!totales) return {};

  // Estructura básica según el esquema XSD: MontoNoFacturable, MontoPeriodo, SaldoAnterior, MontoAvancePago, ValorPagar, TotalITBISRetenido, TotalISRRetencion, TotalITBISPercepcion, TotalISRPercepcion
  const correctedTotales = {
    MontoNoFacturable: totales.MontoNoFacturable || null,
    MontoPeriodo: totales.MontoPeriodo || null,
    SaldoAnterior: totales.SaldoAnterior || null,
    MontoAvancePago: totales.MontoAvancePago || null,
    ValorPagar: totales.MontoTotal || null,
    TotalITBISRetenido: totales.TotalITBISRetenido || null,
    TotalISRRetencion: totales.TotalISRRetencion || null,
    TotalITBISPercepcion: totales.TotalITBISPercepcion || null,
    TotalISRPercepcion: totales.TotalISRPercepcion || null
  };

  // Eliminar propiedades con valor null
  Object.keys(correctedTotales).forEach(key => {
    if (correctedTotales[key] === null) {
      delete correctedTotales[key];
    }
  });

  return correctedTotales;
}

/**
 * Corrige la estructura del elemento 'Totales' para e-CF tipo 31 (Factura de Crédito Fiscal Electrónica)
 * @param {Object} totales - Datos de la sección Totales
 * @returns {Object} - Datos corregidos
 */
function correctTotales31(totales) {
  if (!totales) return {};

  // Guardar los valores importantes
  const montoTotal = totales.MontoTotal || "0.00";
  const totalITBIS = totales.TotalITBIS || "0.00";
  const totalITBISRetenido = totales.TotalITBISRetenido || "0.00";
  
  // Estructura correcta según el esquema XSD para tipo 31
  // Solo incluir los campos permitidos según el error: MontoNoFacturable, MontoPeriodo, SaldoAnterior, MontoAvancePago, ValorPagar, TotalITBISRetenido, TotalISRRetencion, TotalITBISPercepcion, TotalISRPercepcion
  const correctedTotales = {
    MontoNoFacturable: totales.MontoNoFacturable || null,
    MontoPeriodo: totales.MontoPeriodo || null,
    SaldoAnterior: totales.SaldoAnterior || null,
    MontoAvancePago: totales.MontoAvancePago || null,
    ValorPagar: montoTotal,
    TotalITBISRetenido: totalITBISRetenido,
    TotalISRRetencion: totales.TotalISRRetencion || null,
    TotalITBISPercepcion: totales.TotalITBISPercepcion || null,
    TotalISRPercepcion: totales.TotalISRPercepcion || null
  };

  // Eliminar propiedades con valor null
  Object.keys(correctedTotales).forEach(key => {
    if (correctedTotales[key] === null) {
      delete correctedTotales[key];
    }
  });

  return correctedTotales;
}

/**
 * Corrige la estructura del elemento 'Totales' para e-CF tipo 32 (Factura de Consumo Electrónica)
 * @param {Object} totales - Datos de la sección Totales
 * @returns {Object} - Datos corregidos
 */
function correctTotales32(totales) {
  // Para tipo 32, la estructura es similar a tipo 31
  return correctTotales31(totales);
}

/**
 * Corrige la estructura del elemento 'Totales' para e-CF tipo 33 (Nota de Débito Electrónica)
 * @param {Object} totales - Datos de la sección Totales
 * @returns {Object} - Datos corregidos
 */
function correctTotales33(totales) {
  // Para tipo 33, la estructura es similar a tipo 31
  return correctTotales31(totales);
}

/**
 * Corrige la estructura del elemento 'Totales' para e-CF tipo 34 (Nota de Crédito Electrónica)
 * @param {Object} totales - Datos de la sección Totales
 * @param {Object} itemsData - Datos completos del ECF para calcular retenciones
 * @returns {Object} - Datos corregidos
 */
function correctTotales34(totales, itemsData = null) {
  if (!totales) return {};

  const montoTotal = totales.MontoTotal || "0.00";
  
  // Calcular TotalITBISRetenido desde los ítems
  let totalITBISRetenido = "0.00";
  let hasRetentionStructure = false;
  
  if (itemsData && itemsData.DetallesItems && itemsData.DetallesItems.Item) {
    const items = Array.isArray(itemsData.DetallesItems.Item) 
      ? itemsData.DetallesItems.Item 
      : [itemsData.DetallesItems.Item];
    
    items.forEach(item => {
      if (item.Retencion) {
        hasRetentionStructure = true;
        if (item.Retencion.MontoITBISRetenido) {
          const monto = parseFloat(item.Retencion.MontoITBISRetenido) || 0;
          totalITBISRetenido = (parseFloat(totalITBISRetenido) + monto).toFixed(2);
        }
      }
    });
  }

  const correctedTotales = {
    MontoNoFacturable: totales.MontoNoFacturable || null,
    MontoPeriodo: totales.MontoPeriodo || null,
    SaldoAnterior: totales.SaldoAnterior || null,
    MontoAvancePago: totales.MontoAvancePago || null,
    ValorPagar: montoTotal,
    TotalISRRetencion: totales.TotalISRRetencion || null,
    TotalITBISPercepcion: totales.TotalITBISPercepcion || null,
    TotalISRPercepcion: totales.TotalISRPercepcion || null
  };

  // 🚨 CRÍTICO: Solo incluir TotalITBISRetenido si hay estructura de retención
  if (hasRetentionStructure) {
    correctedTotales.TotalITBISRetenido = totalITBISRetenido;
  }

  // Eliminar propiedades con valor null
  Object.keys(correctedTotales).forEach(key => {
    if (correctedTotales[key] === null) {
      delete correctedTotales[key];
    }
  });

  return correctedTotales;
}

/**
 * Corrige la estructura del elemento 'Totales' para e-CF tipo 41 (Comprobante Electrónico de Compras)
 * @param {Object} totales - Datos de la sección Totales
 * @returns {Object} - Datos corregidos
 */
function correctTotales41(totales) {
  if (!totales) return {};

  // Guardar los valores importantes
  const montoTotal = totales.MontoTotal || "0.00";
  const totalITBISRetenido = totales.TotalITBISRetenido || "0.00";
  
  // Estructura correcta según el esquema XSD para tipo 41
  const correctedTotales = {
    MontoNoFacturable: totales.MontoNoFacturable || null,
    MontoPeriodo: totales.MontoPeriodo || null,
    SaldoAnterior: totales.SaldoAnterior || null,
    MontoAvancePago: totales.MontoAvancePago || null,
    ValorPagar: montoTotal,
    TotalITBISRetenido: totalITBISRetenido,
    TotalISRRetencion: totales.TotalISRRetencion || null,
    TotalITBISPercepcion: totales.TotalITBISPercepcion || null,
    TotalISRPercepcion: totales.TotalISRPercepcion || null
  };

  // Eliminar propiedades con valor null
  Object.keys(correctedTotales).forEach(key => {
    if (correctedTotales[key] === null) {
      delete correctedTotales[key];
    }
  });

  return correctedTotales;
}

/**
 * Corrige la estructura del elemento 'Totales' para e-CF tipo 43-47
 * @param {Object} totales - Datos de la sección Totales
 * @returns {Object} - Datos corregidos
 */
function correctTotales43(totales) { return correctTotales31(totales); }
function correctTotales44(totales) { return correctTotales31(totales); }
function correctTotales45(totales) { return correctTotales31(totales); }
function correctTotales46(totales) { return correctTotales31(totales); }
function correctTotales47(totales) {
  if (!totales) return {};

  // Guardar los valores importantes
  const montoTotal = totales.MontoTotal || "0.00";
  const totalISRRetencion = totales.TotalISRRetencion || "0.00";
  
  // Estructura correcta según el esquema XSD para tipo 47
  const correctedTotales = {
    MontoNoFacturable: totales.MontoNoFacturable || null,
    MontoPeriodo: totales.MontoPeriodo || null,
    SaldoAnterior: totales.SaldoAnterior || null,
    MontoAvancePago: totales.MontoAvancePago || null,
    ValorPagar: montoTotal,
    TotalITBISRetenido: totales.TotalITBISRetenido || null,
    TotalISRRetencion: totalISRRetencion,
    TotalITBISPercepcion: totales.TotalITBISPercepcion || null,
    TotalISRPercepcion: totales.TotalISRPercepcion || null
  };

  // Eliminar propiedades con valor null
  Object.keys(correctedTotales).forEach(key => {
    if (correctedTotales[key] === null) {
      delete correctedTotales[key];
    }
  });

  return correctedTotales;
}

// Exportar la función principal
export default correctECFStructure;